import { computed, inject, type Ref } from 'vue';
import { type Feature } from '@/constants/features';
import { type ValueOf } from '@/types';
import { type AppAbility, type Features, type Modules } from './abilities';
import { PermissionAction, Permission } from '@/constants/permissions';

/**
 * The CASL ability is provided with this key for all components.
 */
export const PERMISSIONS_PROVIDE_KEY = Symbol('permissions');
export type PermissionsProvideValue = Ref<AppAbility | undefined>;

export const FEATURES_PROVIDE_KEY = Symbol('features');
export const SITE_MODULES_PROVIDE_KEY = Symbol('siteModules');

type FeaturesProvideValue = Ref<ValueOf<typeof Feature>[]>;
type ModulesProvideValue = Ref<Features | Modules>;

export type AppCan = AppAbility['can'];

/**
 * A small composable to make permission checks easier.
 */
export const usePermissions = () => {
	const ability = inject<PermissionsProvideValue>(PERMISSIONS_PROVIDE_KEY);
	const features = inject<FeaturesProvideValue>(FEATURES_PROVIDE_KEY);
	const siteModules = inject<ModulesProvideValue>(SITE_MODULES_PROVIDE_KEY);

	const can: AppCan = (...args) => {
		if (!ability?.value?.can) {
			// If the permissions are still loading, prefer to hide the checked
			// UI elements
			return false;
		}

		return ability.value.can(...args);
	};

	/**
	 * Check if the given feature is enabled for the current user.
	 */
	const userHas = (feature: ValueOf<typeof Feature>) => {
		return features?.value?.includes(feature) ?? false;
	};

	/**
	 * Check if the given feature is enabled for the current site.
	 */
	const siteHas = (feature: Features | Modules) => {
		return siteModules?.value?.includes(feature) ?? false;
	};

	/**
	 * Check if the given module is enabled for the current site.
	 */
	const isSuperAdmin = computed(() =>
		can(PermissionAction.HAS_PERMISSION, Permission.IS_SUPER_ADMIN)
	);

	return { can, ability, userHas, isSuperAdmin, siteHas };
};
